import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Hint } from "@/features/form-hint";
import { stageText } from "@/lib/contacts";
import { useSkills } from "@/lib/queries";
import { assignableSkills } from "@/lib/skills";
import {
  MAX_RULE_SKILLS, STAGE_RULE_MODES, describeStageRule, newStageRule, stageRuleModeText,
  stageRulePayload, stageRuleProblem, unusedStages,
} from "@/lib/stage-rules";
import type { RoutingSkill, StageRule } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * "Callers by stage" in a line's settings (ADR-073): a rule per stage saying who takes an inbound
 * caller we know whose contact is in it. Edited in place and sent whole with the rest of the line.
 */
export function StageRulesField({
  value, onChange, tenantId, enabled,
}: {
  value: StageRule[];
  onChange: (rules: StageRule[]) => void;
  /** The line's campaign's tenant: whose skills a rule may name. */
  tenantId: number | null;
  /** Load the skills catalog only while the form is open. */
  enabled: boolean;
}) {
  const catalog = useSkills({ enabled });
  const skills = catalog.data ? assignableSkills(catalog.data, tenantId) : [];
  const free = unusedStages(value);

  const update = (index: number, patch: Partial<StageRule>) =>
    onChange(value.map((rule, i) => (i === index ? stageRulePayload({ ...rule, ...patch }) : rule)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => {
    const guess = skills.find((s) => s.slug === "closer")?.slug ?? skills[0]?.slug;
    const rule = newStageRule(value, guess);
    if (rule) onChange([...value, rule]);
  };

  return (
    <section className="space-y-2" aria-labelledby="line-stage-rules-label">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p id="line-stage-rules-label" className="text-xs font-medium text-text">
            Callers by stage
          </p>
          <p className="text-xs text-text-muted">
            <Hint more="A rule replaces this line's and the number's skills for that caller; their language still counts. A call to a rep's own number, and Previous rep first, still ring that rep first.">
              A known caller's stage can decide who takes the call.
            </Hint>
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={add}
          disabled={free.length === 0 || skills.length === 0}
        >
          <Plus />
          Add a rule
        </Button>
      </div>

      {catalog.isPending ? (
        <Skeleton className="h-20" />
      ) : !catalog.data ? (
        <Callout tone="danger">Could not load the skills. Saving keeps this line's rules as they are.</Callout>
      ) : value.length === 0 ? (
        <p className="rounded-md bg-surface-sunken px-3 py-2 text-xs text-text-muted">
          No rules yet. Every caller is routed by the number they dialed.
        </p>
      ) : (
        <ul className="space-y-2 [&>li]:animate-in [&>li]:fade-in-0 [&>li]:duration-200">
          {value.map((rule, i) => (
            <StageRuleRow
              key={rule.stage}
              rule={rule}
              stages={[rule.stage, ...free]}
              skills={skills}
              onChange={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function StageRuleRow({
  rule, stages, skills, onChange, onRemove,
}: {
  rule: StageRule;
  /** This rule's own stage and every stage no other rule has. */
  stages: string[];
  skills: RoutingSkill[];
  onChange: (patch: Partial<StageRule>) => void;
  onRemove: () => void;
}) {
  const stage = stageText(rule.stage).label;
  const held = new Set(rule.skills.map((s) => s.slug));
  const problem = stageRuleProblem(rule);
  const toggle = (slug: string) =>
    onChange({
      skills: held.has(slug)
        ? rule.skills.filter((s) => s.slug !== slug)
        : [...rule.skills, { slug, min_level: 1 }],
    });

  return (
    <li className="space-y-2.5 rounded-md border border-border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={rule.stage} onValueChange={(v) => onChange({ stage: v })}>
          <SelectTrigger className="w-48" aria-label="Stage">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((s) => (
              <SelectItem key={s} value={s} description={stageText(s).description}>
                {stageText(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={rule.mode}
          onValueChange={(v) => onChange({ mode: v as StageRule["mode"] })}
        >
          <SelectTrigger className="w-44" aria-label={`Who takes ${stage} callers`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAGE_RULE_MODES.map((m) => (
              <SelectItem key={m} value={m} description={stageRuleModeText(m).description}>
                {stageRuleModeText(m).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="ml-auto"
          aria-label={`Remove the rule for ${stage}`}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`Skills for ${stage} callers`}>
        {skills.map((skill) => {
          const lit = held.has(skill.slug);
          return (
            <button
              key={skill.id}
              type="button"
              aria-pressed={lit}
              disabled={!lit && rule.skills.length >= MAX_RULE_SKILLS}
              onClick={() => toggle(skill.slug)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                lit
                  ? "border-accent-8 bg-accent-2 font-medium text-accent-11"
                  : "border-border bg-surface text-text hover:bg-surface-hover",
              )}
            >
              {skill.name}
            </button>
          );
        })}
      </div>

      <label className="flex items-start gap-2 text-xs text-text">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={rule.dialer ?? false}
          onChange={(e) => onChange({ dialer: e.target.checked })}
        />
        <span>
          <span className="block font-medium">Also when the dialer calls</span>
          <span className="block text-text-muted">
            Scheduled callbacks and cadence calls for leads in this stage go to these reps. A rep with these skills on
            this campaign's closer line can take them.
          </span>
        </span>
      </label>

      {rule.mode === "only" && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
          Open the call to anyone after
          <Input
            type="number"
            inputMode="numeric"
            className="h-7 w-20"
            placeholder="never"
            aria-label={`Seconds before anyone may take ${stage} callers`}
            value={rule.fallback_seconds ?? ""}
            onChange={(e) =>
              onChange({ fallback_seconds: e.target.value === "" ? null : Number(e.target.value) })
            }
          />
          seconds
        </label>
      )}

      {problem ? (
        <p role="alert" className="text-xs text-danger-11">{problem}</p>
      ) : (
        <p className="text-xs text-text-muted">{describeStageRule(rule, skills)}</p>
      )}
    </li>
  );
}
